import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Form,
  FormGroup,
  Input,
  Label,
  InputGroup,
  InputGroupText,
  InputGroupAddon,
  Container,
  Row,
  Col,
} from "reactstrap";
import { useParams } from "react-router-dom";
import NotificationAlert from "react-notification-alert";
import { validateUUID, resetPasswordAPI } from "actions/forgetPassword";

//initial fields password and confirm password
const changePasswordFields = {
  password: {
    value: "",
    error: "",
  },
  confirmPassword: {
    value: "",
    error: "",
  },
};
export const ResetPassword = () => {
  const { uuid } = useParams();
  const notificationAlertRef = useRef(null);

  const [resetPassword, setResetPassword] = useState(changePasswordFields);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState("");
  const [resetStatus, setResetStatus] = useState(null);

  //get uuid for validate uuid
  useEffect(() => {
    getValidationUUID(uuid);
  }, [uuid]);

  //validate
  const getValidationUUID = async () => {
    try {
      const res = await validateUUID(uuid);
      if (res?.code === 200) {
        setResetStatus(null); 
      } else {
        setResetStatus("error"); // invalid/expired link
      }
    } catch (err) {
      setResetStatus("error"); // invalid/expired link
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validatePasswordForm()) return;
    setShowLoader(true);

    try {
      const payload = {
        uuid: uuid,
        password: resetPassword.password.value,
      };

      const res = await resetPasswordAPI(payload);
      if (res?.code === 200) {
        setResetStatus("success"); //status
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Reset failed";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", "Reset failed");
    } finally {
      setShowLoader(false);
    }
  };

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const getValidationErrorPassword = (name, value) => {
    switch (name) {
      case "password":
        return !value
          ? "Please enter a password"
          : value.length < 8
          ? "The password must contain 8 characters"
          : resetPassword.confirmPassword.value &&
            value !== resetPassword.confirmPassword.value
          ? "The new password and confirm password must match"
          : "";
      case "confirmPassword":
        return !value
          ? "Please enter a confirm password"
          : resetPassword.password.value &&
            value !== resetPassword.password.value
          ? "The new password and confirm password must match"
          : "";
      default:
        return "";
    }
  };
  //validate the form
  const validatePasswordForm = () => {
    let newPassword = { ...resetPassword };

    Object.keys(newPassword).forEach((key) => {
      newPassword[key] = {
        ...newPassword[key],
        error: getValidationErrorPassword(key, newPassword[key].value),
      };
    });

    setResetPassword(newPassword); // set the error state
    return Object.values(newPassword).every((item) => item.error === "");
  };

  //on password input change
  const handlePasswordInputChange = (fieldName, newValue) => {
    if (fieldName === "password") {
      const strength = getPasswordStrength(newValue);
      setPasswordStrength(strength.label);
    }
    setResetPassword((prev) => ({
      ...prev,
      [fieldName]: {
        value: newValue,
        error: "",
      },
    }));
  };

  //on blur
  const handleOnBlurPassForm = (name, value) => {
    const error = getValidationErrorPassword(name, value);
    setResetPassword((prev) => ({
      ...prev,
      [name]: { ...prev[name], error },
    }));
  };

  //check the strength of password
  const getPasswordStrength = (password) => {
    if (!password) return { label: "", score: 0 };

    //initialize the score as 0
    let score = 0;

    if (password.length >= 8) score++; //length must me > 8
    if (/[A-Z]/.test(password)) score++; // if atleast one uppercase +1
    if (/[a-z]/.test(password)) score++; // if atleast one lowercase +1
    if (/[0-9]/.test(password)) score++; // if atleast one number +1
    if (/[^A-Za-z0-9]/.test(password)) score++; //test score

    if (score <= 2) return { label: "Weak", score };
    if (score === 3 || score === 4) return { label: "Medium", score };

    return { label: "Strong", score };
  };

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <div className="reset-wrapper">
        <Container>
          <Row className="justify-content-center">
            <Col md="6" lg="5" xl="5">
              <Card className="reset-card">
                <CardHeader className="reset-card-header text-center">
                  <CardTitle tag="h4">Reset Password</CardTitle>
                </CardHeader>

                <CardBody>
                  {resetStatus ? (
                    <div className="uuid-icon-container">
                      {resetStatus === "success" && (
                        <>
                          <i className="fa fa-check-circle uuid-icon success"></i>
                          <p className="uuid-message">
                            Password Reset Successfully
                          </p>
                        </>
                      )}
                      {resetStatus === "error" && (
                        <>
                          <i className="fa fa-exclamation-triangle uuid-icon warning"></i>
                          <p className="uuid-message">Password reset link expired or invalid</p>
                        </>
                      )}
                    </div>
                  ) : (
                    <Form onSubmit={handleSubmit}>
                      {/* New Password */}
                      <FormGroup
                        className={`mt-4 ${
                          resetPassword.password.error ? "has-danger" : ""
                        } `}
                      >
                        <Label for="newPassword">
                          New Password <span className="text-danger">*</span>
                        </Label>
                        <InputGroup>
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                              <i className="fa fa-lock" />
                            </InputGroupText>
                          </InputGroupAddon>
                          <Input
                            name="password"
                            type={showPassword ? "text" : "password"}
                            placeholder="New Password"
                            value={resetPassword.password.value}
                            onChange={(e) =>
                              handlePasswordInputChange(
                                e.target.name,
                                e.target.value
                              )
                            }
                            onBlur={(e) =>
                              handleOnBlurPassForm(
                                e.target.name,
                                e.target.value
                              )
                            }
                            required
                          />
                          <InputGroupAddon addonType="append">
                            <InputGroupText
                              className="pointer"
                              onClick={() => setShowPassword((prev) => !prev)}
                            >
                              {showPassword ? (
                                <i className="fa fa-eye-slash" />
                              ) : (
                                <i className="fa fa-eye" />
                              )}
                            </InputGroupText>
                          </InputGroupAddon>
                        </InputGroup>
                        {resetPassword.password.error && (
                          <small className="validation-error">
                            {resetPassword.password.error}
                          </small>
                        )}
                      </FormGroup>
                      {/* Confirm Password */}
                      <FormGroup
                        className={`has-label ${
                          resetPassword.confirmPassword.error
                            ? "has-danger"
                            : ""
                        } d-flex flex-column align-items-start mb-4`}
                      >
                        <Label for="confirmPassword">
                          Confirm New Password{" "}
                          <span className="text-danger">*</span>
                        </Label>
                        <InputGroup>
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText>
                              <i className="fa fa-lock" />
                            </InputGroupText>
                          </InputGroupAddon>

                          <Input
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm Password"
                            value={resetPassword.confirmPassword.value}
                            onChange={(e) =>
                              handlePasswordInputChange(
                                e.target.name,
                                e.target.value
                              )
                            }
                            onBlur={(e) =>
                              handleOnBlurPassForm(
                                e.target.name,
                                e.target.value
                              )
                            }
                            required
                          />
                          <InputGroupAddon addonType="append">
                            <InputGroupText
                              className="pointer"
                              onClick={() =>
                                setShowConfirmPassword((prev) => !prev)
                              }
                            >
                              {showConfirmPassword ? (
                                <i className="fa fa-eye-slash" />
                              ) : (
                                <i className="fa fa-eye" />
                              )}
                            </InputGroupText>
                          </InputGroupAddon>
                        </InputGroup>
                        {resetPassword.confirmPassword.error && (
                          <small className="validation-error">
                            {resetPassword.confirmPassword.error}
                          </small>
                        )}
                      </FormGroup>

                      {/* //check password strength and display : strong weak medium */}
                      <div className="password-strength">
                        Password strength:
                        <span
                          className={
                            passwordStrength === "Strong"
                              ? "text-success"
                              : passwordStrength === "Medium"
                              ? "text-warning"
                              : "text-danger"
                          }
                        >
                          {" "}
                          {passwordStrength}
                        </span>
                      </div>
                      <Button
                        type="submit"
                        color="default"
                        className="reset-btn"
                        disabled={
                          showLoader ||
                          passwordStrength === "Weak" ||
                          Object.values(resetPassword).some(
                            (item) => item.error !== ""
                          )
                        }
                      >
                        {showLoader ? "Resetting..." : "Reset Password"}
                      </Button>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};
